/**
 * @genesis_version V9.1
 * @genesis_hub The Application Route Scaffold
 * @description
 * Root of the React application — establishes the provider nesting order, the flat
 * declarative route tree, and the code-split lazy-loading boundaries for every page.
 * Orchestrates the three-tier route guard architecture: PublicOnlyGuard (auth pages),
 * unprotected recovery routes (auth callback, password reset), and ProtectedRoute
 * (all authenticated content including workspace-scoped dashboards).
 * V17.1: Single Semantic Canvas — .theme-canvas applied at React root (AppContent wrapper).
 * Layout components manage geometry only; React root owns the canvas physics.
 * @blast_radius
 * - Downstream: Every page render, every provider (Auth, Query, Sidebar, Theme),
 *   every lazy-loaded route chunk. A broken provider order or guard misconfiguration
 *   breaks the entire application shell.
 * - Upstream: AuthProvider (session context), QueryClientProvider (TanStack cache),
 *   ThemeProvider (theme cascade), all domain page components.
 * @invariants
 * - Provider nesting order is EXACT and immovable: ErrorBoundary → QueryClientProvider →
 *   AuthProvider → SidebarProvider → Router → ThemeProvider (inside BrowserRouter).
 * - Route tree MUST be flat and declarative (conditional ternaries are a BLOCKING VIOLATION
 *   per @genesis_ai_trail V9.0).
 * - All domain pages MUST use React.lazy() with code-splitting for Vite chunk optimization
 *   (Rule 9, Part 3.4).
 * - Recovery routes (/auth/callback, /update-password, /reset-password) MUST remain
 *   outside both PublicOnlyGuard and ProtectedRoute to prevent session race conditions.
 * - React root wrapper is the sole owner of .theme-canvas (Single Semantic Canvas, V17.1).
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { ErrorBoundary } from './components/feedback/ErrorBoundary.js';
import { GlassLoadingSpinner } from './components/feedback/GlassLoadingSpinner.js';
import { RouteErrorBoundary } from './components/feedback/RouteErrorBoundary.js';
import { ProtectedRoute } from './components/guards/ProtectedRoute.js';
import { PublicOnlyGuard } from './components/guards/PublicOnlyGuard.js';
import { AuthProvider } from './context/AuthContext.js';
import { SidebarProvider } from './context/SidebarContext.js';
import { queryClient } from './core/api/queryClient.js';
import { ThemeProvider } from './core/theme/ThemeProvider.js';
import { AuthCallback } from './pages/auth/AuthCallback.js';
import { Login } from './pages/auth/Login.js';
import { ProfileCreation } from './pages/auth/ProfileCreation.js';
import { Signup } from './pages/auth/Signup.js';

// Lazy load page components for code splitting
const UpdatePassword = lazy(() =>
  import('./pages/auth/UpdatePassword.js').then(m => ({ default: m.UpdatePassword }))
);

const ResetPassword = lazy(() =>
  import('./pages/auth/ResetPassword.js').then(m => ({ default: m.ResetPassword }))
);

const RecoveryPassword = lazy(() =>
  import('./pages/auth/RecoveryPassword.js').then(m => ({ default: m.RecoveryPassword }))
);

const PersonalWorkspaceRedirect = lazy(() =>
  import('./domains/workspaces/components/PersonalWorkspaceRedirect.js').then(m => ({ default: m.PersonalWorkspaceRedirect }))
);
const RecipeDetail = lazy(() =>
  import('./domains/recipes/pages/RecipeDetail.js').then(m => ({ default: m.RecipeDetail }))
);
const Categories = lazy(() =>
  import('./domains/categories/components/Categories.js').then(m => ({ default: m.Categories }))
);
const CaptureMenu = lazy(() =>
  import('./domains/ai/pages/capture/CaptureMenu.js').then(m => ({ default: m.CaptureMenu }))
);
const UrlCapturePage = lazy(() =>
  import('./domains/ai/pages/capture/UrlCapturePage.js').then(m => ({ default: m.UrlCapturePage }))
);
const TextCapturePage = lazy(() =>
  import('./domains/ai/pages/capture/TextCapturePage.js').then(m => ({ default: m.TextCapturePage }))
);
const VisionCapturePage = lazy(() =>
  import('./domains/ai/pages/capture/VisionCapturePage.js').then(m => ({ default: m.VisionCapturePage }))
);
const FileCapturePage = lazy(() =>
  import('./domains/ingestion/pages/FileCapturePage.js').then(m => ({ default: m.FileCapturePage }))
);
const WorkspaceLayout = lazy(() =>
  import('./domains/workspaces/components/WorkspaceLayout.js').then(m => ({ default: m.default }))
);
const SharedWorkspaceView = lazy(() =>
  import('./domains/workspaces/pages/SharedWorkspaceView.js').then(m => ({ default: m.default }))
);
const WorkspaceMembers = lazy(() =>
  import('./domains/workspaces/pages/WorkspaceMembers.js').then(m => ({ default: m.default }))
);
const WorkspaceSettings = lazy(() =>
  import('./domains/workspaces/pages/WorkspaceSettings.js').then(m => ({ default: m.default }))
);
const WorkspaceSelection = lazy(() =>
  import('./domains/workspaces/pages/WorkspaceSelection.js').then(m => ({ default: m.default }))
);

const SettingsLayout = lazy(() =>
  import('@/core/layout/SettingsLayout.js').then(m => ({ default: m.SettingsLayout }))
);


const AccountView = lazy(() =>
  import('@/domains/settings/components/AccountView.js').then(m => ({ default: m.AccountView }))
);

const SecurityView = lazy(() =>
  import('@/domains/settings/components/SecurityView.js').then(m => ({ default: m.SecurityView }))
);

const AppearanceView = lazy(() =>
  import('@/domains/settings/components/AppearanceView.js').then(m => ({ default: m.AppearanceView }))
);

const ExportsView = lazy(() =>
  import('@/domains/settings/components/ExportsView.js').then(m => ({ default: m.ExportsView }))
);

const DashboardLayout = lazy(() =>
  import('@/core/layout/DashboardLayout.js').then(m => ({ default: m.DashboardLayout }))
);

function AppContent() {
  return (
    <div className="min-h-screen flex flex-col theme-canvas text-[var(--color-canvas-text)] font-sans transition-colors duration-300">
      <BrowserRouter>
        <Suspense
          fallback={
            <div className="min-h-screen flex flex-col items-center justify-center theme-canvas">
              <GlassLoadingSpinner
                message="Loading page..."
                size="md"
                fullScreen={false}
              />
            </div>
          }
        >
          <ThemeProvider>
            <Routes>
              {/* Anonymous-Only Views — redirect to / if already authenticated */}
              <Route element={<PublicOnlyGuard />}>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/profile" element={<ProfileCreation />} />
              </Route>

              {/* Unprotected / Ephemeral Recovery Views — bypass all guards to prevent session race conditions */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/update-password" element={<RecoveryPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Strictly Protected Application Shell */}
              <Route element={<ProtectedRoute />}>
                <Route element={<DashboardLayout />}>
                  {/* Root route: Loads Personal Workspace (isPersonal=true) */}
                  <Route path="/" element={<PersonalWorkspaceRedirect />} />

                  <Route path="/recipes/:id" element={<RecipeDetail />} errorElement={<RouteErrorBoundary />} />
                  <Route path="/categories" element={<Categories />} errorElement={<RouteErrorBoundary />} />
                  {/* Settings — Master-Detail sidebar layout with four sub-views */}
                  <Route path="/settings" element={<SettingsLayout />} errorElement={<RouteErrorBoundary />}>
                    <Route index element={<Navigate to="account" replace />} />
                    <Route path="account" element={<AccountView />} errorElement={<RouteErrorBoundary />} />
                    <Route path="security" element={<SecurityView />} errorElement={<RouteErrorBoundary />} />
                    <Route path="appearance" element={<AppearanceView />} errorElement={<RouteErrorBoundary />} />
                    <Route path="exports" element={<ExportsView />} errorElement={<RouteErrorBoundary />} />
                  </Route>

                  {/* Capture Routes - Isolated single-focus pages for each capture vector */}
                  <Route path="/capture" element={<CaptureMenu />} errorElement={<RouteErrorBoundary />} />
                  <Route path="/capture/url" element={<UrlCapturePage />} errorElement={<RouteErrorBoundary />} />
                  <Route path="/capture/text" element={<TextCapturePage />} errorElement={<RouteErrorBoundary />} />
                  <Route path="/capture/vision" element={<VisionCapturePage />} errorElement={<RouteErrorBoundary />} />
                  <Route path="/capture/file" element={<FileCapturePage />} errorElement={<RouteErrorBoundary />} />

                  {/* Workspace Selection Page */}
                  <Route path="/workspaces" element={<WorkspaceSelection />} errorElement={<RouteErrorBoundary />} />

                  {/* Dynamic Shell: Intercepts active tenancy layout views */}
                  <Route path="/workspaces/:id" element={<WorkspaceLayout />} errorElement={<RouteErrorBoundary />}>
                    <Route index element={<SharedWorkspaceView />} errorElement={<RouteErrorBoundary />} />
                    <Route path="members" element={<WorkspaceMembers />} errorElement={<RouteErrorBoundary />} />
                    <Route path="settings" element={<WorkspaceSettings />} errorElement={<RouteErrorBoundary />} />
                    <Route path="categories" element={<Categories />} errorElement={<RouteErrorBoundary />} />
                  </Route>
                </Route>
              </Route>

              {/* Global Catch-All Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ThemeProvider>
        </Suspense>
      </BrowserRouter>

      {/* Global Toaster for app-wide toast notifications */}
      <Toaster
        position="bottom-center"
        richColors
        closeButton
        duration={4000}
      />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}